import { useReactiveVar } from '@apollo/client';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import {
  NavigationContainer,
  useNavigationContainerRef,
} from '@react-navigation/native';
import { theme } from 'aimo-ui';
import Clarity from 'analytics/Clarity';
import VehicleAdd from 'components/account/vehicle/VehicleAdd';
import VehicleCameraRecognition from 'components/account/vehicle/VehicleCameraRecognition';
import Icon from 'components/icons/Icon';
import { Logo } from 'components/map/MapButtons';
import Modal from 'components/modal/Modal';
import LongTermParking from 'components/park/longTermParking/LongTermParking';
import config from 'config/config';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useCompanyBenefitNotification from 'hooks/useCompanyBenefitNotification';
import useIsMobile from 'hooks/useIsMobile';
import useOpenScreenOnAppStartUp from 'hooks/useOpenScreenOnAppStartUp';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { enableScreens } from 'react-native-screens';
import AdyenRedirect from 'screens/payment/AdyenRedirect.web';
import ParkingAndChargingHistoryDetailsScreen from 'screens/account/ParkingAndChargingHistoryDetailsScreen';
import ParkingHistoryDetailsScreen from 'screens/account/ParkingHistoryDetailsScreen';
import WebLoginSuccess from 'screens/account/WebLoginSuccess';
import ApproveTerms from 'screens/approveTerms/ApproveTerms';
import ApproveTermsAppLocation from 'screens/approveTerms/ApproveTermsAppLocation';
import ApproveUpdatedTermsScreen from 'screens/approveTerms/ApproveUpdatedTermsScreen';
import ChargingReportScreen from 'screens/ChargingReportScreen';
import HslTicketScreen from 'screens/hslTicket/HslTicketScreen';
import LandingScreen from 'screens/LandingScreen';
import ParkingSpaceMapViewerScreen from 'screens/longTermParking/ParkingSpaceMapViewerScreen';
import BenefitVerificationScreen from 'screens/account/BenefitVerificationScreen';
import ParkingReceiptScreen from 'screens/ParkingReceiptScreen';
import MultiPoolPermit from 'screens/permit/access/MultiPoolPermit';
import PermitSelectVehicle from 'screens/permit/access/PermitSelectVehicle';
import PoolingGroup from 'screens/permit/access/PoolingGroup';
import Permit from 'screens/permit/Permit';
import Permits from 'screens/permit/Permits';
import SalesOrderPermitDetails from 'screens/permit/salesOrder/SalesOrderPermitDetails';
import SalesOrderPermitEdit from 'screens/permit/salesOrder/SalesOrderPermitEdit';
import { showLandingScreen } from 'states/persistInStorage';
import { useAppConfigStore } from 'stores/appConfigStore';
import { useUiStore } from 'stores/uiStore';
import { webNavigationStyle } from 'styles/StyleSheetStyles';
import { RootStackParamList } from 'types/navigation';
import { titleFormatter } from 'utils/commonUtils';
import { isWeb } from 'utils/deviceUtils';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { getTabBarIndicator } from 'utils/indicatorUtils';
import { createStackNavigator } from 'utils/navigationHelpers';
import {
  beforeRemoveApproveTerms,
  devScreen,
  getModalOptions,
  NavigationSettingsType,
  onFocusApproveTerms,
} from 'utils/navigationUtils';
import screens from '../screens';
import CityParkingNavigator from './CityParkingNavigator';
import ModalStackHeader from './ModalStackHeader';
import {
  StackHeaderComponent,
  StackHeaderComponentNoHeaderBack,
} from './navigationComponents';
import OnboardingNavigator from './OnboardingNavigator';
import StackHeader from './StackHeader';

enableScreens(false);
const Tab = createBottomTabNavigator();
const Stack = createStackNavigator();

const TabsNavigatorScreen = ({ nav }: { nav: NavigationSettingsType }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    flags: { ride },
  } = useContext(FeatureFlagsContext);
  const showRide = showFeatureFlag(ride);

  const { activeTintColor, inactiveTintColor } = nav;
  let { screenArray } = nav;
  if (config.devScreen === 'true') {
    screenArray = [...screenArray, devScreen];
  }
  const tabBarIndicatorsList = useUiStore((state) => state.tabBarIndicators);

  // Feature Flag - ride menu item
  if (!showRide) {
    screenArray = screenArray.filter(
      (screenProps) => screenProps.screenName !== 'rideScreen'
    );
  }

  const tabBarStyle = isMobile
    ? webNavigationStyle.tabContainerMobile
    : undefined;

  const tabBarLabelPosition = isMobile ? 'below-icon' : 'beside-icon';

  const tabBarItemRightMargin = (isMobile ? 0 : 1) * 120;

  const tabBarLeftMargin = (isMobile ? 1 : 2) * 120;

  return (
    <>
      <Logo testID="aimo-logo" />
      <Tab.Navigator backBehavior="history">
        {screenArray.map(
          (
            {
              tabLocalizationKey,
              screenName,
              iconName,
              iconNameActive,
              initialParams,
              screenTitle,
            },
            index: number
          ) => {
            const screenComponent: React.FC = screens[screenName];
            return (
              <Tab.Screen
                key={`tab-screen-${index}`}
                name={screenName}
                component={screenComponent}
                initialParams={initialParams}
                options={{
                  title: `${screenTitle || screenName}`,
                  tabBarButtonTestID: tabLocalizationKey,
                  tabBarLabel: t(tabLocalizationKey),
                  tabBarPosition: 'top',
                  tabBarStyle: [webNavigationStyle.tabContainer, tabBarStyle],
                  tabBarActiveTintColor: activeTintColor,
                  tabBarInactiveTintColor: inactiveTintColor,

                  tabBarLabelStyle: [
                    webNavigationStyle.label,
                    isMobile ? webNavigationStyle.labelMobile : undefined,
                  ],
                  tabBarLabelPosition: tabBarLabelPosition,
                  tabBarHideOnKeyboard: true,
                  tabBarItemStyle: [
                    index === 0 ? { marginLeft: tabBarLeftMargin } : undefined,
                    index === screenArray.length - 1
                      ? { marginRight: tabBarItemRightMargin }
                      : undefined,
                  ],
                  headerShown: false,
                  tabBarIcon: ({ focused, color }) => {
                    const tabBarIndicator = getTabBarIndicator(
                      tabBarIndicatorsList,
                      tabLocalizationKey
                    );

                    return (
                      <Icon
                        name={focused ? iconNameActive : iconName}
                        color={color}
                        size="l"
                        indicatorType={tabBarIndicator?.indicatorType}
                        indicatorNumber={tabBarIndicator?.indicatorNumber}
                        testID={`tab-icon-${tabLocalizationKey}`}
                      />
                    );
                  },
                }}
              />
            );
          }
        )}
      </Tab.Navigator>
    </>
  );
};

const Navigation = ({ navigation }: { navigation: NavigationSettingsType }) => {
  const navigationRef = useNavigationContainerRef<RootStackParamList>();
  const { t } = useTranslation();

  const [navigationReady, setNavigationReady] = useState<boolean>(false);
  const { handleCompanyBenefitNotification } = useCompanyBenefitNotification();
  useOpenScreenOnAppStartUp(navigationReady, navigationRef);
  const { linking } = navigation;
  const isFirstLaunch = useReactiveVar(showLandingScreen);

  const modalOptions = useMemo(() => getModalOptions(), []);

  useEffect(() => {
    if (navigationReady) {
      handleCompanyBenefitNotification(navigationRef);
    }
  }, [handleCompanyBenefitNotification, navigationReady, navigationRef]);

  const TabNav = useCallback(
    () => <TabsNavigatorScreen nav={navigation} />,
    [navigation]
  );

  const modalFullScreenOptions = useMemo(
    () => getModalOptions(undefined, true),
    []
  );

  const modalPartialScreenOptions = useMemo(
    () => getModalOptions('70%', true),
    []
  );

  return (
    <View style={webNavigationStyle.backgroundContainer}>
      <NavigationContainer
        linking={linking}
        ref={navigationRef}
        navigationInChildEnabled //Will be removed in version 8 https://reactnavigation.org/docs/upgrading-from-6.x#changes-to-the-navigate-action
        onReady={() => {
          setNavigationReady(true);
          useAppConfigStore
            .getState()
            .setCurrentRoute(navigationRef?.getCurrentRoute());
          if (isFirstLaunch) {
            setTimeout(
              () => navigationRef.current?.navigate('landingScreen'),
              0
            );
          }
        }}
        onStateChange={async () => {
          useAppConfigStore
            .getState()
            .setCurrentRoute(navigationRef?.getCurrentRoute());
        }}
        documentTitle={{
          formatter: (options, route) => titleFormatter(options, route),
        }}>
        <Stack.Navigator
          screenOptions={{
            headerShown: false,
            presentation: 'card',
          }}>
          <Stack.Screen name="mainTabs" component={TabNav} />
          <Stack.Screen name="cityParking" component={CityParkingNavigator} />
          <Stack.Screen
            name="onboardingModal"
            component={OnboardingNavigator}
          />
          <Stack.Screen
            name="parkingDetails"
            component={ParkingHistoryDetailsScreen}
            options={{
              headerShown: true,
              headerBackButtonDisplayMode: 'minimal',
              headerTransparent: true,
              header: (props) => (
                <ModalStackHeader
                  {...props}
                  background={theme.colors.transparent}
                  closeButton={false}
                  backButtonType="secondary"
                />
              ),
            }}
          />
          <Stack.Screen
            name="parkAndChargeDetails"
            component={ParkingAndChargingHistoryDetailsScreen}
            options={{
              headerShown: true,
              headerBackButtonDisplayMode: 'minimal',
              headerTransparent: true,
              header: (props) => (
                <ModalStackHeader
                  {...props}
                  background={theme.colors.transparent}
                  closeButton={false}
                  backButtonType="secondary"
                />
              ),
            }}
          />
          <Stack.Screen
            name="parkingReceipt"
            component={ParkingReceiptScreen}
          />
          <Stack.Screen
            name="benefitVerification"
            component={BenefitVerificationScreen}
          />
          <Stack.Screen
            name="chargingReport"
            component={ChargingReportScreen}
            options={{
              title: t('evCharging.receipt.sheetHeader'),
            }}
          />
          <Stack.Screen
            name="landingScreen"
            component={LandingScreen}
            options={{
              title: t('landingScreen.title'),
            }}
          />
          <Stack.Screen
            name="loginSuccess"
            component={WebLoginSuccess}
            options={{
              title: t('account.signIn'),
              headerShown: true,
              header: StackHeaderComponentNoHeaderBack,
              cardStyle: { backgroundColor: theme.colors.white },
            }}
          />
          {/* WEB-only Adyen 3DS2 redirect return route (CPS-26589 B-T8). The
              issuer redirects back to <origin>/adyen-redirect; this screen
              resumes the card-add (submit redirectResult, poll to Active) then
              forwards to payment-methods. Headerless full-page like loginSuccess.
              Native has no equivalent route (3DS2 returns in-app). */}
          <Stack.Screen
            name="adyenRedirect"
            component={AdyenRedirect}
            options={{
              headerShown: false,
              cardStyle: { backgroundColor: theme.colors.white },
            }}
          />
          <Stack.Screen
            name="approveTerms"
            component={ApproveTerms}
            options={{
              title: t('account.terms.approveTerms'),
              headerShown: true,
              header: StackHeaderComponentNoHeaderBack,
            }}
            listeners={{
              focus: () => {
                onFocusApproveTerms();
              },
              beforeRemove: () => {
                beforeRemoveApproveTerms();
              },
            }}
          />
          <Stack.Screen
            name="approveUpdatedTerms"
            component={ApproveUpdatedTermsScreen}
            options={{
              headerShown: false,
            }}
            listeners={{
              beforeRemove: () => {
                useUiStore.getState().setIsAppStartupScreenOpen(false);
              },
            }}
          />
          <Stack.Screen
            name="addVehicleParking"
            component={VehicleAdd}
            options={{
              title: t('account.addVehicle'),
              headerShown: true,
              cardStyle: {
                backgroundColor: theme.colors.white,
              },
              header: StackHeaderComponent,
            }}
          />
          <Stack.Group
            screenOptions={{
              presentation: 'modal',
              header: (props) => (
                <ModalStackHeader {...props} {...modalOptions.headerStyle} />
              ),
            }}>
            <Stack.Screen
              name="vehicleCameraRecognitionParking"
              component={VehicleCameraRecognition}
              options={{
                title: t('vehicle.cameraRecognition.title'),
                headerShown: true,
                cardStyle: modalOptions.cardStyle,
              }}
            />
            <Stack.Screen
              name="approveTermsAppLocation"
              component={ApproveTermsAppLocation}
              options={{
                title: t('account.terms.termsAgreement'),
                headerShown: true,
                cardStyle: {
                  backgroundColor: theme.colors.white,
                },
                header: (props) => (
                  <StackHeader background={theme.colors.white} {...props} />
                ),
              }}
              listeners={{
                focus: () => {
                  onFocusApproveTerms();
                },
                beforeRemove: () => {
                  beforeRemoveApproveTerms();
                },
              }}
            />
          </Stack.Group>
          <Stack.Group
            screenOptions={{
              headerBackButtonDisplayMode: 'minimal',
              header: (props) => (
                <StackHeader
                  {...props}
                  buttonType="tertiary"
                  background={theme.colors.white}
                />
              ),
            }}>
            <Stack.Screen
              name="longTermParking"
              component={LongTermParking}
              options={{
                headerMode: 'float',
                headerShown: false,
                headerBackButtonDisplayMode: 'minimal',
                title: t('longTermParking.search'),
              }}
            />
            <Stack.Screen
              name="parkingSpaceMapViewer"
              component={ParkingSpaceMapViewerScreen}
              options={{
                title: t('longTermParking.selectParkingSpace'),
                headerMode: 'float',
                headerShown: false,
                headerBackButtonDisplayMode: 'minimal',
              }}
            />
          </Stack.Group>
          <Stack.Group
            screenOptions={{
              headerShown: true,
              header: (props) => (
                <StackHeader
                  {...props}
                  buttonType="secondary"
                  background={theme.colors.gray100}
                />
              ),
            }}>
            <Stack.Screen
              name="hslTicket"
              component={HslTicketScreen}
              options={{
                headerShown: true,
                title: t('hslTicket.title'),
              }}
            />
          </Stack.Group>
          <Stack.Group
            screenOptions={{
              headerShown: true,
              header: (props) => (
                <StackHeader
                  {...props}
                  buttonType="secondary"
                  background={theme.colors.transparent}
                />
              ),
            }}>
            <Stack.Screen
              name="salesOrderPermitDetails"
              component={SalesOrderPermitDetails}
              options={{
                headerBackButtonDisplayMode: 'minimal',
                headerTransparent: false,
                headerShown: true,
                cardStyle: {
                  backgroundColor: theme.colors.white,
                },
              }}
            />
            <Stack.Screen
              name="permit"
              component={Permit}
              options={{
                presentation: 'modal',
                //title is set dynamically in the component depending on permitType
                headerBackButtonDisplayMode: 'minimal',
              }}
            />
            <Stack.Screen
              name="multiPoolingPermit"
              component={MultiPoolPermit}
              options={{
                presentation: 'modal',
                headerBackButtonDisplayMode: 'minimal',
              }}
            />
            <Stack.Screen
              name="poolingGroup"
              component={PoolingGroup}
              options={{
                presentation: 'modal',
                headerBackButtonDisplayMode: 'minimal',
              }}
            />
            <Stack.Screen
              name="permits"
              component={Permits}
              options={{
                title: t('services.permits'),
                headerBackButtonDisplayMode: 'minimal',
                headerTransparent: false,
                headerShown: true,
                cardStyle: {
                  backgroundColor: theme.colors.gray100,
                },
              }}
            />
            <Stack.Screen
              name="salesOrderPermitEdit"
              component={SalesOrderPermitEdit}
              options={{
                presentation: 'modal',
                title: t('permit.editPermit'),
                headerShown: false,
              }}
            />
            <Stack.Screen
              name="permitVehicles"
              component={PermitSelectVehicle}
              options={{
                ...modalFullScreenOptions.screenOptions,
                title: t('parking.parkingProcess.selectVehicle'),
                headerBackButtonDisplayMode: 'minimal',
                cardStyle: isWeb
                  ? modalFullScreenOptions.cardStyle
                  : modalPartialScreenOptions.cardStyle,
              }}
            />
          </Stack.Group>
        </Stack.Navigator>
        <Modal />
        <Clarity />
      </NavigationContainer>
    </View>
  );
};

export default Navigation;
